/**
 * InstalledAppsPage Component
 *
 * View and manage installed marketplace applications with update checking.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowPathIcon,
  ArrowUpCircleIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { DashboardLayout } from '../../../components/layout/DashboardLayout';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { Alert } from '../../../components/ui/Alert';
import { useInstalledMarketplaceApps, InstalledMarketplaceApp } from '../hooks/useInstalledMarketplaceApps';
import { useUpgradeMarketplaceApp } from '../hooks/useUpgradeMarketplaceApp';
import { useAuth } from '../../../contexts/AuthContext';
import { hasPermission } from '../../../lib/utils/permissions';
import { useLayoutProps } from '../../../hooks/useLayoutProps';
import { getErrorMessage } from '../../../lib/utils/errors';

/**
 * Format date for display
 */
const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * InstalledAppsPage component
 */
export const InstalledAppsPage: React.FC = () => {
  const navigate = useNavigate();
  const { tenant, user, activeRole } = useAuth();
  const layoutProps = useLayoutProps();

  const tenantId = tenant?.tenantId || '';

  // Permission checks
  const canManage = hasPermission(activeRole?.permissions, 'marketplace:app:update');

  const {
    data: installedApps = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useInstalledMarketplaceApps(tenantId);

  const upgradeMutation = useUpgradeMarketplaceApp();

  // Count apps with available updates
  const appsWithUpdates = React.useMemo(() => {
    return installedApps.filter((app) => app.updateAvailable);
  }, [installedApps]);

  const handleUpgrade = async (app: InstalledMarketplaceApp) => {
    if (!app.latestVersion) return;
    try {
      await upgradeMutation.mutateAsync({
        tenantId,
        applicationId: app.applicationId,
        data: { targetVersion: app.latestVersion },
      });
    } catch (err) {
      console.error('Upgrade failed:', err);
    }
  };

  const handleViewInMarketplace = (app: InstalledMarketplaceApp) => {
    const listingId = (app as InstalledMarketplaceApp & { marketplaceMetadata?: { listingId: string } }).marketplaceMetadata?.listingId;
    if (listingId) {
      navigate(`/app/marketplace/${listingId}`);
    }
  };

  return (
    <DashboardLayout {...layoutProps}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Installed Marketplace Apps</h1>
            <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              Manage applications installed from the marketplace
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="secondary"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              <ArrowPathIcon className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button onClick={() => navigate('/app/marketplace')}>
              Browse Marketplace
            </Button>
          </div>
        </div>

        {/* Updates Banner */}
        {appsWithUpdates.length > 0 && (
          <Alert variant="info">
            <div className="flex items-center">
              <ArrowUpCircleIcon className="h-5 w-5 mr-2" />
              <span>
                {appsWithUpdates.length} update{appsWithUpdates.length !== 1 ? 's' : ''} available
              </span>
            </div>
          </Alert>
        )}

        {/* Error Alert */}
        {error && (
          <Alert variant="error">
            {getErrorMessage(error)}
          </Alert>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="text-center py-12">
            <ArrowPathIcon className="h-8 w-8 animate-spin mx-auto" style={{ color: 'var(--color-text-muted)' }} />
            <p className="mt-2" style={{ color: 'var(--color-text-secondary)' }}>Loading installed apps...</p>
          </div>
        )}

        {/* Apps List */}
        {!isLoading && installedApps.length > 0 && (
          <div className="card rounded-lg overflow-hidden" style={{ borderColor: 'var(--color-border)' }}>
            <table className="min-w-full divide-y" style={{ borderColor: 'var(--color-border)' }}>
              <thead style={{ backgroundColor: 'var(--color-bg-sunken)' }}>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-secondary)' }}>
                    Application
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-secondary)' }}>
                    Publisher
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-secondary)' }}>
                    Version
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-secondary)' }}>
                    Installed
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-secondary)' }}>
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-secondary)' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{ backgroundColor: 'var(--color-bg-raised)', borderColor: 'var(--color-border)' }}>
                {installedApps.map((app) => {
                  const marketplaceMetadata = (app as InstalledMarketplaceApp & { marketplaceMetadata?: { publisherTenantCode: string; installedVersion: string; installedAt: string } }).marketplaceMetadata;

                  return (
                    <tr key={app.applicationId}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center mr-3">
                            <span className="text-primary-600 font-semibold">
                              {app.applicationName.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <div className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>{app.applicationName}</div>
                            <div className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{app.applicationCode}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                        {marketplaceMetadata?.publisherTenantCode || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm" style={{ color: 'var(--color-text-primary)' }}>
                          {marketplaceMetadata?.installedVersion || app.version || '?'}
                        </span>
                        {app.updateAvailable && app.latestVersion && (
                          <Badge variant="warning" size="sm" className="ml-2">
                            {app.latestVersion} available
                          </Badge>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                        {marketplaceMetadata?.installedAt
                          ? formatDate(marketplaceMetadata.installedAt)
                          : formatDate(app.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {app.updateAvailable ? (
                          <Badge variant="warning">
                            <ExclamationCircleIcon className="w-3 h-3 mr-1" />
                            Update Available
                          </Badge>
                        ) : (
                          <Badge variant="success">
                            <CheckCircleIcon className="w-3 h-3 mr-1" />
                            Up to Date
                          </Badge>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          {app.updateAvailable && canManage && (
                            <Button
                              size="sm"
                              onClick={() => handleUpgrade(app)}
                              disabled={upgradeMutation.isPending}
                            >
                              {upgradeMutation.isPending ? 'Upgrading...' : 'Upgrade'}
                            </Button>
                          )}
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleViewInMarketplace(app)}
                          >
                            View
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && installedApps.length === 0 && (
          <div className="text-center py-12 card rounded-lg" style={{ borderColor: 'var(--color-border)' }}>
            <ArrowPathIcon className="h-12 w-12 mx-auto" style={{ color: 'var(--color-text-muted)' }} />
            <h3 className="mt-2 text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>No marketplace apps installed</h3>
            <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              Browse the marketplace to find and install applications
            </p>
            <Button
              className="mt-4"
              onClick={() => navigate('/app/marketplace')}
            >
              Browse Marketplace
            </Button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default InstalledAppsPage;
